import { JobStatus } from '@applicator/shared'
import { useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'


import AddJobDrawer from './AddJobDrawer'
import FilterBar from './FilterBar'
import FlowStageTabs from './FlowStageTabs/FlowStageTabs'
import JobsTable from './JobsTable'
import RelevanceFilter, { RelevanceFilterType } from './RelevanceFilter'

// Re-export for backward compatibility
export type { JobStatus }

function MainContent() {
  const [searchParams] = useSearchParams()
  const searchQueryId = searchParams.get('searchQueryId')
  const relevanceFilterParam = searchParams.get('relevanceFilter') as RelevanceFilterType | null
  const [activeStatus, setActiveStatus] = useState<JobStatus | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [relevanceFilter, setRelevanceFilter] = useState<RelevanceFilterType>(
    relevanceFilterParam && ['all', 'relevant', 'non-relevant', 'not-analyzed'].includes(relevanceFilterParam)
      ? relevanceFilterParam
      : 'all'
  )

  // Update relevanceFilter when URL parameter changes
  useEffect(() => {
    if (relevanceFilterParam && ['all', 'relevant', 'non-relevant', 'not-analyzed'].includes(relevanceFilterParam)) {
      setRelevanceFilter(relevanceFilterParam)
    }
  }, [relevanceFilterParam])
  const [statusCounts, setStatusCounts] = useState<Record<JobStatus, number>>({
    Bookmarked: 0,
    Applying: 0,
    Applied: 0,
    Interviewing: 0,
    Negotiating: 0,
    Accepted: 0,
    'I Withdrew': 0,
    'Not Selected': 0,
    'No Response': 0,
    Archived: 0,
  })
  const addJobRef = useRef<(() => void) | null>(null)
  const bulkDeleteRef = useRef<(() => void) | null>(null)
  const bulkAddToJobsRef = useRef<(() => void) | null>(null)
  const [selectedCount, setSelectedCount] = useState(0)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const refreshJobsRef = useRef<(() => void) | null>(null)
  const [relevanceCounts, setRelevanceCounts] = useState<{
    relevant: number
    nonRelevant: number
    notAnalyzed: number
    total: number
  }>({
    relevant: 0,
    nonRelevant: 0,
    notAnalyzed: 0,
    total: 0,
  })

  // Преобразуем статусы в формат для FlowStageTabs
  const stages = [
    { key: 'Bookmarked', label: 'Bookmarked', count: statusCounts.Bookmarked > 0 ? statusCounts.Bookmarked : null },
    { key: 'Applying', label: 'Applying', count: statusCounts.Applying > 0 ? statusCounts.Applying : null },
    { key: 'Applied', label: 'Applied', count: statusCounts.Applied > 0 ? statusCounts.Applied : null },
    { key: 'Interviewing', label: 'Interviewing', count: statusCounts.Interviewing > 0 ? statusCounts.Interviewing : null },
    { key: 'Negotiating', label: 'Negotiating', count: statusCounts.Negotiating > 0 ? statusCounts.Negotiating : null },
    { key: 'Accepted', label: 'Accepted', count: statusCounts.Accepted > 0 ? statusCounts.Accepted : null },
  ]

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="p-6 pb-0 flex-shrink-0">
        {searchQueryId && (
          <button
            onClick={() => {
              window.location.href = '/search-queries'
            }}
            className="mb-2 flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <span>&lt;</span>
            <span>Back to Search Queries</span>
          </button>
        )}
        <div className="mb-4">
          <FlowStageTabs
            stages={stages}
            activeKey={activeStatus || ''}
            onChange={(key) => {
              if (key === activeStatus) {
                setActiveStatus(null)
              } else {
                setActiveStatus(key as JobStatus)
              }
            }}
          />
        </div>
        {/* Combined Relevance Filter, Search, and Add Job Button */}
        <div className="mb-4 flex items-center gap-4">
          {/* Relevance Filter - Left */}
          <RelevanceFilter 
            value={relevanceFilter}
            onChange={setRelevanceFilter}
            counts={relevanceCounts}
          />
          
          {/* Search Input - Center */}
          <div className="flex-1">
            <FilterBar 
              searchQuery={searchQuery} 
              onSearchChange={setSearchQuery}
              onAddJob={() => setIsDrawerOpen(true)}
              onBulkDelete={() => bulkDeleteRef.current?.()}
              onBulkAddToJobs={() => bulkAddToJobsRef.current?.()}
              selectedCount={selectedCount}
              showAddButton={false}
              showBulkAddToJobs={!!searchQueryId}
            />
          </div>
          
          {/* Add Job Button - Right */}
          <button 
            onClick={() => setIsDrawerOpen(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-md flex items-center gap-2 text-sm font-medium hover:bg-green-700 whitespace-nowrap"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span>Add a New Job</span>
          </button>
        </div>
      </div>
        <div className="flex-1 flex flex-col px-6 pb-6 min-h-0" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <JobsTable
            activeStatus={activeStatus}
            searchQuery={searchQuery}
            searchQueryId={searchQueryId}
            relevanceFilter={relevanceFilter}
            onStatusChange={setActiveStatus}
            onStatusCountsChange={setStatusCounts}
            onRelevanceCountsChange={setRelevanceCounts}
            onAddJobRef={(fn) => { addJobRef.current = fn }}
            onBulkDeleteRef={(fn) => { bulkDeleteRef.current = fn }}
            onBulkAddToJobsRef={(fn) => { bulkAddToJobsRef.current = fn }}
            onSelectedCountChange={setSelectedCount}
            onRefreshRef={(fn) => { refreshJobsRef.current = fn }}
          />
        </div>
      <AddJobDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onJobAdded={() => {
          refreshJobsRef.current?.()
          setIsDrawerOpen(false)
        }}
      />
    </div>
  )
}

export default MainContent
